/**
 * Toast — переиспользуемая система notification'ов для Onboardix.
 *
 * API:
 *   const { toast } = useToast()
 *   toast.success('Сохранено')                    // auto-dismiss 4s
 *   toast.info('Обновление доступно')             // auto-dismiss 4s
 *   toast.warning('ИС истекает через 3 дня')      // auto-dismiss 6s
 *   toast.error('Не удалось загрузить отчёт')     // auto-dismiss 8s
 *   toast.warning('Сейчас сохраняется...', { duration: 0 })  // persistent (manual dismiss)
 *   toast.info('Доступна новая версия', {
 *     action: { label: 'Обновить', onClick: () => updateSW() },
 *   })
 *   toast.dismiss(id)   // manual close one
 *   toast.clear()       // close all
 *
 * Mount: один <ToastProvider> на корне App (обнимает Routes).
 *
 * Position: top-right desktop, top-center mobile (≤640px).
 * Stack: max 5 видимых, остальные в очереди FIFO.
 * Animation: slide-in from edge + opacity, slide-out.
 *
 * Apple-style: компактные карточки `--shadow-lg`, тинт-фон `--color-{tone}-bg`,
 * иконка Lucide (size=18) монохромная в `--color-{tone}`.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react'

const ToastContext = createContext(null)

/** Default dismiss durations per tone, in ms. 0 = persistent. */
const DEFAULT_DURATION = {
    success: 4000,
    info:    4000,
    warning: 6000,
    danger:  8000,
}

const TONE_ICON = {
    success: CheckCircle2,
    info:    Info,
    warning: AlertTriangle,
    danger:  AlertCircle,
}

const TONE_COLOR = {
    success: 'var(--color-success)',
    info:    'var(--color-info)',
    warning: 'var(--color-warning)',
    danger:  'var(--color-danger)',
}

const TONE_BG = {
    success: 'var(--color-success-bg)',
    info:    'var(--color-info-bg)',
    warning: 'var(--color-warning-bg)',
    danger:  'var(--color-danger-bg)',
}

const MAX_VISIBLE = 5

let _id = 0
const nextId = () => ++_id

export function ToastProvider({ children }) {
    const [items, setItems] = useState([])
    const timeouts = useRef(new Map())

    const dismiss = useCallback((id) => {
        setItems((prev) => prev.filter((t) => t.id !== id))
        const tm = timeouts.current.get(id)
        if (tm) {
            clearTimeout(tm)
            timeouts.current.delete(id)
        }
    }, [])

    const clear = useCallback(() => {
        timeouts.current.forEach(clearTimeout)
        timeouts.current.clear()
        setItems([])
    }, [])

    const push = useCallback((tone, message, opts = {}) => {
        const id = nextId()
        const duration = opts.duration ?? DEFAULT_DURATION[tone]
        const item = {
            id,
            tone,
            message,
            action: opts.action || null,
            createdAt: Date.now(),
        }
        setItems((prev) => [...prev, item])
        if (duration > 0) {
            const tm = setTimeout(() => dismiss(id), duration)
            timeouts.current.set(id, tm)
        }
        return id
    }, [dismiss])

    const toast = useMemo(() => ({
        success: (msg, opts) => push('success', msg, opts),
        info:    (msg, opts) => push('info',    msg, opts),
        warning: (msg, opts) => push('warning', msg, opts),
        danger:  (msg, opts) => push('danger',  msg, opts),
        error:   (msg, opts) => push('danger',  msg, opts),
        dismiss,
        clear,
    }), [push, dismiss, clear])

    useEffect(() => () => {
        timeouts.current.forEach(clearTimeout)
        timeouts.current.clear()
    }, [])

    return (
        <ToastContext.Provider value={toast}>
            {children}
            <ToastContainer items={items} onDismiss={dismiss} />
        </ToastContext.Provider>
    )
}

export function useToast() {
    const ctx = useContext(ToastContext)
    if (!ctx) {
        throw new Error('useToast must be used inside <ToastProvider>')
    }
    return { toast: ctx }
}

function ToastContainer({ items, onDismiss }) {
    if (typeof document === 'undefined') return null
    const visible = items.slice(-MAX_VISIBLE)
    return createPortal(
        <div
            aria-live="polite"
            aria-atomic="true"
            style={{
                position: 'fixed',
                top: 16,
                right: 16,
                left: 'auto',
                zIndex: 'var(--z-toast)',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                padding: 16,
                pointerEvents: 'none',
                maxWidth: 'min(420px, calc(100vw - 32px))',
            }}
            data-onboardix-toast-container
        >
            {visible.map((t) => (
                <ToastItem key={t.id} item={t} onDismiss={onDismiss} />
            ))}
        </div>,
        document.body,
    )
}

function ToastItem({ item, onDismiss }) {
    const Icon = TONE_ICON[item.tone] || Info
    const [closing, setClosing] = useState(false)
    const [closeHovered, setCloseHovered] = useState(false)

    const handleClose = useCallback(() => {
        setClosing(true)
        // wait for fade-out animation before unmount
        setTimeout(() => onDismiss(item.id), 160)
    }, [item.id, onDismiss])

    return (
        <div
            role="status"
            style={{
                pointerEvents: 'auto',
                background: 'var(--color-bg-elevated)',
                border: `1px solid var(--color-border)`,
                borderLeft: `2px solid ${TONE_COLOR[item.tone]}`,
                borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--elevation-3, var(--shadow-lg))',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                padding: '10px 12px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                fontSize: 13,
                lineHeight: 1.4,
                color: 'var(--color-text)',
                opacity: closing ? 0 : 1,
                transform: closing ? 'translateX(8px)' : 'translateX(0)',
                transition: 'opacity 160ms var(--ease-standard), transform 160ms var(--ease-standard)',
                animation: closing ? 'none' : 'onb-toast-in 240ms var(--ease-standard)',
            }}
        >
            <span
                style={{
                    flexShrink: 0,
                    color: TONE_COLOR[item.tone],
                    background: TONE_BG[item.tone],
                    borderRadius: '50%',
                    padding: 4,
                    display: 'inline-flex',
                }}
                aria-hidden="true"
            >
                <Icon size={16} />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>{item.message}</span>
            {item.action && (
                <button
                    type="button"
                    className="btn btn--soft-accent btn--sm"
                    style={{ flexShrink: 0 }}
                    onClick={() => {
                        try { item.action.onClick() } finally { handleClose() }
                    }}
                >
                    {item.action.label}
                </button>
            )}
            <button
                type="button"
                onClick={handleClose}
                onMouseEnter={() => setCloseHovered(true)}
                onMouseLeave={() => setCloseHovered(false)}
                aria-label="Закрыть"
                style={{
                    flexShrink: 0,
                    padding: 4,
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--color-text-muted)',
                    cursor: 'pointer',
                    borderRadius: 'var(--radius-sm)',
                    display: 'inline-flex',
                    opacity: closeHovered ? 1 : 0.7,
                    transition: 'opacity 150ms ease',
                }}
            >
                <X size={14} />
            </button>
        </div>
    )
}

/* Inject keyframes once. Тут проще, чем заводить отдельный .css файл. */
if (typeof document !== 'undefined' && !document.getElementById('onb-toast-keyframes')) {
    const style = document.createElement('style')
    style.id = 'onb-toast-keyframes'
    style.textContent = `
@keyframes onb-toast-in {
    from { opacity: 0; transform: translateX(24px); }
    to   { opacity: 1; transform: translateX(0); }
}
@media (max-width: 640px) {
    [data-onboardix-toast-container] {
        right: 12px !important;
        left: 12px !important;
        top: 12px !important;
        max-width: none !important;
    }
}
/* Снижение движения — общий блок в index.css, здесь не дублируется. */
`
    document.head.appendChild(style)
}
